import { useEffect, useRef } from 'react';

import { useGlobalMediaStore } from './use-global-media-store';
import { usePriorityMedia } from './use-priority-media';

type MediaElement = HTMLVideoElement | HTMLAudioElement;

type MediaOrchestrationOptions = {
	bonusId?: string; // ID of the bonus this media belongs to
	focused?: boolean; // Whether the media is in focus mode (e.g., in a modal)
};

/**
 * Hook to orchestrate media playback based on viewport visibility and user interactions.
 * Handles auto-play behavior for media elements crossing the center of the viewport.
 * When focused=true, will prioritize playback for media matching the URL parameters.
 *
 * @param mediaRef Reference to the media element
 * @param options Configuration options including bonusId and focused state
 */
export const useMediaOrchestration = (mediaRef: MediaElement | null, options?: MediaOrchestrationOptions) => {
	const { playVideo, pauseVideo, registerElement, unregisterElement, hasPriorityVideo, setModalOpen } = useGlobalMediaStore();
	const observerRef = useRef<IntersectionObserver | null>(null);
	const centerObserverRef = useRef<IntersectionObserver | null>(null);

	// Determine if this media should be prioritized based on URL parameters
	const shouldPrioritize = usePriorityMedia(options?.bonusId, options?.focused || false);

	// If this is a focused video, update the modal state
	useEffect(() => {
		if (options?.focused && options?.bonusId) {
			setModalOpen(true, options.bonusId);

			return () => setModalOpen(false);
		}
	}, [options?.focused, options?.bonusId, setModalOpen]);

	useEffect(() => {
		if (!mediaRef) return;

		const eventHandlers = [
			{ event: 'play', handler: () => playVideo(mediaRef) },
			{ event: 'pause', handler: () => pauseVideo(mediaRef) },
		] as const;

		eventHandlers.forEach(({ event, handler }) => {
			mediaRef.addEventListener(event, handler);
		});

		// ===============================================
		// VISIBILITY OBSERVER: Tracks when media enters/exits viewport
		// Used to register/unregister the element with the media store
		// ===============================================
		const handleIntersection = (entries: IntersectionObserverEntry[]) => {
			const entry = entries[0];

			// When at least 30% of the media is visible (matches threshold setting below),
			// we register it with the media store
			if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
				registerElement(mediaRef);
			} else {
				unregisterElement(mediaRef);
			}
		};

		// Set observer to trigger the callback when media is at least 30% visible
		observerRef.current = new IntersectionObserver(handleIntersection, {
			root: null,
			rootMargin: '0px',
			threshold: 0.3, // This 0.3 (30%) value is what gets compared in the intersectionRatio check above
		});

		observerRef.current.observe(mediaRef);

		// ===============================================
		// CENTER OBSERVER: Detects when media crosses center of viewport
		// Used to auto-play the media when it becomes the focal point
		// ===============================================
		const handleCenterIntersection = (entries: IntersectionObserverEntry[]) => {
			const entry = entries[0];
			registerElement(mediaRef);
			// Simple prioritization:
			// 1. Focused videos always play when they match URL params
			// 2. Feed videos only play if they don't have a modal counterpart
			if (options?.focused && shouldPrioritize) {
				playVideo(mediaRef);
			} else if (entry.isIntersecting && !(options?.bonusId && hasPriorityVideo(options.bonusId))) {
				playVideo(mediaRef);
			}
		};

		centerObserverRef.current = new IntersectionObserver(handleCenterIntersection, {
			root: null,
			rootMargin: '-40% 0px -40% 0px', // Creates a narrow 20% height band in the middle of the viewport
			threshold: 0, // Trigger as soon as any part of the element enters the center band
		});

		centerObserverRef.current.observe(mediaRef);

		return () => {
			eventHandlers.forEach(({ event, handler }) => {
				mediaRef.removeEventListener(event, handler);
			});

			if (observerRef.current) observerRef.current.disconnect();
			if (centerObserverRef.current) centerObserverRef.current.disconnect();

			unregisterElement(mediaRef);
		};
	}, [mediaRef, playVideo, pauseVideo, registerElement, unregisterElement, options?.bonusId, options?.focused, shouldPrioritize, hasPriorityVideo]);
};
